import { View, Text } from "../../components/shared/Themed";
import { StyleSheet, TouchableOpacity, TextInput, Platform } from "react-native";
import { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import * as Okta from "../../services/oktaAuthService";
import { ReadOnlyProps } from "../../utils/templates";
import appColors from "../../utils/constants/colors";
import { useNavigation } from "@react-navigation/native";
import { Feather } from "@expo/vector-icons";

interface Props {
  setDisplayForgetPasswordScreen: (e: boolean) => void;
  setDisplayLoginWithTendaScreen: (e: boolean) => void;
}

export const LoginScreen = ({
  setDisplayForgetPasswordScreen,
  setDisplayLoginWithTendaScreen,
}: ReadOnlyProps<Props>) => {
  const navigation = useNavigation();
  const [isLoading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [keepLogged, setKeepLogged] = useState<boolean>(true);

  // Inject CSS to override browser autofill blue highlight (web only)
  useEffect(() => {
    if (Platform.OS === "web") {
      const style = document.createElement("style");
      style.textContent = `
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active,
        textarea:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 30px rgba(255,255,255,0.5) inset !important;
          box-shadow: 0 0 0 30px rgba(255,255,255,0.5) inset !important;
          -webkit-text-fill-color: #1a1a2e !important;
          background-color: rgba(255,255,255,0.5) !important;
          transition: background-color 5000s ease-in-out 0s;
        }
        input, input:active, input:hover,
        textarea, textarea:active {
          outline: none !important;
          -webkit-appearance: none;
        }
        input:focus, textarea:focus {
          outline: none !important;
          border: 1.5px solid rgba(255,255,255,0.9) !important;
          border-radius: 10px !important;
          box-shadow: 0 0 8px rgba(97,184,255,0.3) !important;
        }
        [data-testid="passwordWrapper"] > input:focus {
          border: none !important;
          outline: none !important;
          box-shadow: none !important;
        }
        [data-testid="passwordWrapper"]:focus-within {
          border: 1.5px solid rgba(255,255,255,0.9) !important;
          border-radius: 10px !important;
          box-shadow: 0 0 8px rgba(97,184,255,0.3) !important;
        }
      `;
      document.head.appendChild(style);
      return () => {
        document.head.removeChild(style);
      };
    }
  }, []);

  const loginDefaultValues = {
    userEmail: "",
    userPassword: "",
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: loginDefaultValues,
  });

  const onSubmit = (formData: any) => {
    setLoading(true);
    Okta.getAuth(formData.userEmail.trim(), formData.userPassword.trim()).then(
      (data: any) => {
        setStatus(data.status);
        setLoading(false);

        //Reset the form
        if (data.status !== "FAILED") {
          reset(loginDefaultValues);
          navigation.reset({
            index: 0,
            routes: [{ name: "Root" }],
          });
        }
      }
    );
  };

  return (
    <View style={styles.container}>
      {/* Login Heading */}
      <Text style={[styles.heading, { backgroundColor: "transparent" }]}>Login</Text>

      {/* Email Input */}
      <Controller
        control={control}
        rules={{
          validate: {
            required: async (value) => {
              if (!value) return "* This is required.";
            },
          },
        }}
        name="userEmail"
        render={({ field: { onChange, value } }) => (
          <TextInput
            style={styles.input}
            placeholder="Email"
            placeholderTextColor="#aaa"
            value={value}
            onChangeText={onChange}
            blurOnSubmit={false}
            onSubmitEditing={handleSubmit(onSubmit)}
            autoCapitalize="none"
            autoComplete="off"
            selectionColor="#999"
          />
        )}
      />
      {errors.userEmail && (
        <Text style={styles.errorText}>* This is required.</Text>
      )}

      {/* Password Input */}
      <Controller
        control={control}
        rules={{
          validate: {
            required: async (value) => {
              if (!value) return "* This is required.";
            },
          },
        }}
        name="userPassword"
        render={({ field: { onChange, value } }) => (
          <View testID="passwordWrapper" style={styles.passwordWrapper}>
            <TextInput
              style={styles.passwordInput}
              placeholder="Password"
              placeholderTextColor="#aaa"
              value={value}
              onChangeText={onChange}
              secureTextEntry={!showPassword}
              blurOnSubmit={false}
              onSubmitEditing={handleSubmit(onSubmit)}
              autoComplete="off"
              selectionColor="#999"
            />
            <TouchableOpacity
              onPress={() => setShowPassword(!showPassword)}
              style={styles.eyeIcon}
            >
              <Feather
                name={showPassword ? "eye" : "eye-off"}
                size={18}
                color="#aaa"
              />
            </TouchableOpacity>
          </View>
        )}
      />
      {errors.userPassword && (
        <Text style={styles.errorText}>* This is required.</Text>
      )}

      {/* Error status */}
      {status === "FAILED" && (
        <Text style={[styles.errorText, { marginTop: 8 }]}>
          * Invalid username or password.
        </Text>
      )}

      {/* Forgot Password row */}
      <View style={[styles.forgotRow, { backgroundColor: "transparent" }]}>
        <View />
        <TouchableOpacity
          onPress={() => setDisplayForgetPasswordScreen(true)}
        >
          <Text style={[styles.forgotLink, { backgroundColor: "transparent" }]}>Forgot password?</Text>
        </TouchableOpacity>
      </View>

      {/* Login Button */}
      <TouchableOpacity
        style={styles.loginButton}
        onPress={handleSubmit(onSubmit)}
        activeOpacity={0.8}
      >
        <Text style={[styles.loginButtonText, { backgroundColor: "transparent" }]}>
          {isLoading ? "Logging in..." : "Login"}
        </Text>
      </TouchableOpacity>

      {/* "Or login with" divider */}
      <View style={[styles.dividerRow, { backgroundColor: "transparent", marginTop: 24 }]}>
        <View style={styles.dividerLine} />
        <Text style={[styles.dividerText, { backgroundColor: "transparent" }]}>Or login with</Text>
        <View style={styles.dividerLine} />
      </View>

      {/* Login with TANDA button */}
      <TouchableOpacity
        style={styles.tandaButton}
        onPress={() => setDisplayLoginWithTendaScreen(true)}
        activeOpacity={0.7}
      >
        <Text style={[styles.tandaButtonText, { backgroundColor: "transparent" }]}>Login with TANDA</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingTop: 10,
    paddingBottom: 25,
    backgroundColor: "transparent",
  },
  heading: {
    fontSize: 26,
    fontWeight: "bold",
    color: "#1a1a2e",
    textAlign: "center",
    marginBottom: 30,
  },
  tandaButton: {
    borderWidth: 1,
    borderColor: "rgba(180,200,220,0.6)",
    borderRadius: 10,
    height: 48,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.75)",
    marginBottom: 24,
  },
  tandaButtonText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#1a1a2e",
  },
  dividerRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 24,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: "rgba(255,255,255,0.5)",
  },
  dividerText: {
    marginHorizontal: 12,
    fontSize: 13,
    color: "#5a6a7a",
  },
  input: {
    backgroundColor: "rgba(255,255,255,0.75)",
    borderRadius: 10,
    height: 48,
    paddingHorizontal: 16,
    fontSize: 14,
    color: "#1a1a2e",
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "rgba(180,200,220,0.6)",
    outlineStyle: "none",
    outlineWidth: 0,
    outlineColor: "transparent",
  } as any,
  passwordWrapper: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "rgba(255,255,255,0.75)",
    borderRadius: 10,
    height: 48,
    marginBottom: 16,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: "rgba(180,200,220,0.6)",
  },
  passwordInput: {
    flex: 1,
    fontSize: 14,
    color: "#1a1a2e",
    height: "100%",
    borderWidth: 0,
    backgroundColor: "transparent",
    outlineStyle: "none",
    outlineWidth: 0,
    outlineColor: "transparent",
  } as any,
  eyeIcon: {
    padding: 4,
  },
  errorText: {
    color: "#d32f2f",
    fontSize: 12,
    marginTop: -8,
    marginBottom: 8,
  },
  forgotRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 24,
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.7)",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
    backgroundColor: "rgba(255,255,255,0.4)",
  },
  checkboxActive: {
    backgroundColor: "#61B8FF",
    borderColor: "#61B8FF",
  },
  keepLoggedText: {
    fontSize: 13,
    color: "#3a4a5a",
  },
  forgotLink: {
    fontSize: 13,
    color: "#0066CC",
    fontWeight: "600",
  },
  loginButton: {
    backgroundColor: "#61B8FF",
    borderRadius: 10,
    height: 48,
    justifyContent: "center",
    alignItems: "center",
  },
  loginButtonText: {
    color: "#fff",
    fontSize: 15,
    fontWeight: "700",
    letterSpacing: 0.5,
  },
});
